import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, Truck, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

export default function CatalogShippingAudit() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/catalog/missing-shipping-data");
            setData(data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    if (loading) return <div className="text-white/55">Loading audit…</div>;
    if (!data) return <div className="text-red-400">Failed to load audit data.</div>;

    const { issues_count, total_active_products, fallback_triggers_count, dimensions_missing_count, rows } = data;

    return (
        <div data-testid="admin-shipping-audit-page" className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <Link to="/admin" className="text-[11px] uppercase tracking-[0.2em] text-white/45 inline-flex items-center gap-1.5 hover:text-white">
                        <ArrowLeft size={12} /> Back to dashboard
                    </Link>
                    <h1 className="font-display text-4xl font-black tracking-tight text-white mt-1">Catalog shipping audit</h1>
                    <p className="mt-2 text-sm text-white/60 max-w-2xl">
                        Active products missing the data needed for accurate shipping. Products that trigger
                        the Rs.99 fallback are listed first. Variant products are OK when every active variant
                        has its own <span className="font-mono">shipping_weight_g</span>.
                    </p>
                </div>
                <button
                    data-testid="shipping-audit-refresh-btn"
                    onClick={fetchData}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[#27272A] text-white/60 hover:text-white hover:bg-[#1a1a1e]"
                >
                    <RotateCcw className="h-4 w-4" /> Refresh
                </button>
            </div>

            {/* Summary cards */}
            <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">Active products</div>
                    <div className="mt-1 text-2xl font-bold text-white font-mono">{total_active_products}</div>
                </div>
                <div className={`bg-[#121214] border rounded-lg p-4 ${fallback_triggers_count > 0 ? "border-amber-500/40" : "border-[#27272A]"}`}>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400/80">Fallback shipping</div>
                    <div className="mt-1 text-2xl font-bold font-mono">
                        <span data-testid="audit-fallback-count" className={fallback_triggers_count > 0 ? "text-amber-400" : "text-white"}>{fallback_triggers_count}</span>
                    </div>
                    <div className="text-xs text-white/55 mt-0.5">Would charge Rs.99 placeholder</div>
                </div>
                <div className="bg-[#121214] border border-[#27272A] rounded-lg p-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-white/45">No dimensions</div>
                    <div className="mt-1 text-2xl font-bold text-white font-mono">{dimensions_missing_count}</div>
                    <div className="text-xs text-white/55 mt-0.5">Volumetric weight risk</div>
                </div>
            </div>

            {issues_count === 0 ? (
                <div className="bg-[#121214] border border-emerald-500/30 rounded-lg p-8 text-center" data-testid="audit-all-clean">
                    <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
                    <div className="mt-3 text-white font-bold text-lg">All active products have shipping data ✓</div>
                    <div className="mt-1 text-sm text-white/55">No fallback charges, no volumetric risk.</div>
                </div>
            ) : (
                <div className="bg-[#121214] border border-[#27272A] rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-[11px] uppercase tracking-widest font-display text-white/55 bg-black/40">
                                <tr>
                                    <th className="px-4 py-3">Product</th>
                                    <th className="px-4 py-3">Category</th>
                                    <th className="px-4 py-3">Brand</th>
                                    <th className="px-4 py-3 text-right">Stock</th>
                                    <th className="px-4 py-3 text-right">Weight (g)</th>
                                    <th className="px-4 py-3 text-right">L×W×H (cm)</th>
                                    <th className="px-4 py-3">Fallback?</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t border-[#27272A]" data-testid={`audit-row-${r.slug}`}>
                                        <td className="px-4 py-3">
                                            <div className="text-white font-medium">{r.name}</div>
                                            <div className="text-[11px] text-white/40 font-mono mt-0.5">
                                                {r.slug}
                                                {r.product_type === "variant" ? " · variant" : ""}
                                            </div>
                                            {r.variants_missing_weight?.length > 0 && (
                                                <div className="text-[11px] text-amber-400/80 mt-0.5">
                                                    Missing weight on variant{r.variants_missing_weight.length > 1 ? "s" : ""}:{" "}
                                                    <span className="font-mono">{r.variants_missing_weight.join(", ")}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-white/70">{r.category || "—"}</td>
                                        <td className="px-4 py-3 text-white/70">{r.brand || "—"}</td>
                                        <td className="px-4 py-3 text-right font-mono text-white/70">{r.stock}</td>
                                        <td className={`px-4 py-3 text-right font-mono ${r.weight_grams > 0 ? "text-white/70" : "text-amber-400 font-bold"}`}>
                                            {r.weight_grams > 0 ? r.weight_grams : "—"}
                                        </td>
                                        <td className={`px-4 py-3 text-right font-mono text-xs ${r.dimensions_set ? "text-white/70" : "text-amber-400"}`}>
                                            {r.dimensions_set ? `${r.length_cm}×${r.width_cm}×${r.height_cm}` : "— · — · —"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {r.triggers_fallback_shipping ? (
                                                <span className="inline-flex items-center gap-1 text-amber-400 text-xs font-semibold">
                                                    <AlertTriangle size={12} /> Yes
                                                </span>
                                            ) : (
                                                <span className="text-white/45 text-xs">No</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                to={`/admin/products/${r.id}`}
                                                data-testid={`audit-edit-${r.slug}`}
                                                className="text-[#00BCE5] hover:underline text-xs font-semibold uppercase tracking-wider"
                                            >
                                                Edit →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="flex items-start gap-2 text-xs text-white/45 max-w-3xl">
                <Truck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                    Once a product has a positive <span className="font-mono">weight_grams</span> (or every active
                    variant has its own <span className="font-mono">shipping_weight_g</span>), checkout will compute
                    real shipping instead of the Rs.99 placeholder. Dimensions become important when Shiprocket
                    goes live — couriers may rate-charge on volumetric weight.
                </span>
            </div>
        </div>
    );
}
